import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Shield, Truck, Headphones, Zap, CheckCircle2, MapPin, Clock, CreditCard, Sparkles, TrendingUp, Lock, Activity } from "lucide-react";
import ReviewsSection from "../components/ReviewsSection";
import { api } from "../lib/api";
import { TrustScore } from "../components/StarRating";
import PriceCalculatorHero from "../components/PriceCalculatorHero";
import HeroReviewsWidget from "../components/HeroReviewsWidget";
import { useT } from "../lib/i18n";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/e2e5cc9d-b2f7-4604-b128-95d6d58354a9/images/6c4669671535b01e627098e08122599d0d005d168b0be9fe490a5484704203e4.png";
const COURIER_IMG = "https://images.unsplash.com/photo-1543499459-d1460946bdc6";
const STREET_IMG = "https://images.unsplash.com/photo-1520683499094-581c1f52cd67";
const B2B_IMG = "https://images.pexels.com/photos/33175650/pexels-photo-33175650.jpeg";

export default function HomePage() {
  const [trust, setTrust] = React.useState({ average: 0, count: 0 });
  const t = useT();
  React.useEffect(() => {
    api.get("/reviews/public", { params: { limit: 1 } }).then(({ data }) => setTrust(data.stats || {})).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* === HERO === */}
      <section className="relative overflow-hidden grain min-h-[92vh] flex flex-col" data-testid="hero-section">
        <div className="absolute inset-0">
          <video
            src="/brand/hero-video.mp4"
            poster={HERO_IMG}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
            data-testid="hero-video"
          />
          <img src={HERO_IMG} alt="EMA Kurer levering" className="w-full h-full object-cover absolute inset-0 -z-10" />
          <div className="absolute inset-0 hero-overlay" />
          <div className="absolute inset-0 bg-gradient-to-b from-brand-dark/40 via-transparent to-brand-dark/85" />
        </div>

        {/* Hero starts directly — clean premium look */}

        <div className="relative z-10 max-w-7xl mx-auto w-full px-5 sm:px-8 lg:px-12 pt-16 pb-24 lg:pt-24 lg:pb-32 flex-1 flex items-center">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 text-white/80 fade-up bg-white/5 border border-white/10 backdrop-blur-md px-4 py-2" style={{ color: "#fca5a5" }}>
              <Sparkles size={14} />
              <span className="text-[11px] tracking-[0.22em] uppercase font-bold">{t("home.hero_kicker")} · Privat & Erhverv</span>
            </div>
            <h1 className="font-display font-bold text-white text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight mt-7 fade-up delay-1">
              <span className="text-brand-red">{t("home.hero_title_a")}</span><br/>
              {t("home.hero_title_b")}
            </h1>
            <p className="text-white/80 text-lg sm:text-xl max-w-2xl mt-7 leading-relaxed fade-up delay-2">
              {t("home.hero_subtitle")}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4 fade-up delay-3">
              <Link to="/book" data-testid="hero-cta-book" className="btn-primary text-base">
                {t("home.cta_book")} <ArrowRight size={18} />
              </Link>
              <Link to="/erhverv" data-testid="hero-cta-business" className="btn-ghost-light">
                {t("home.cta_business")}
              </Link>
            </div>

            {/* Inline trust score */}
            {trust.count > 0 && (
              <div className="mt-10 inline-flex items-center gap-4 bg-white/95 text-brand-dark px-5 py-3 fade-up delay-4" data-testid="hero-trust">
                <TrustScore stats={trust} compact />
                <span className="text-xs uppercase tracking-widest text-brand-text2">verificerede kunder</span>
              </div>
            )}

            {/* Reviews widget — Trustpilot-style social proof */}
            <div className="mt-6 fade-up delay-5">
              <HeroReviewsWidget />
            </div>
          </div>
        </div>

        {/* Bottom stat row */}
        <div className="relative z-10 border-t border-white/10 bg-brand-dark/70 backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-6 grid grid-cols-2 lg:grid-cols-4 gap-6 text-white">
            {[
              { k: "Privat", v: "Same Day · Express · Aften" },
              { k: "Erhverv", v: "Portal · API · CSV · Labels · Tracking" },
              { k: "11 KG", v: "maks vægt pr. kolli" },
              { k: "Sjælland", v: "fuld dækning" },
            ].map((s, i) => (
              <div key={s.k} className={`fade-up delay-${i + 1}`}>
                <div className="font-display font-bold text-2xl sm:text-3xl tracking-tight">{s.k}</div>
                <div className="text-white/50 text-[10px] uppercase tracking-[0.22em] mt-1">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === PRICE CALCULATOR === */}
      <section className="bg-brand-dark relative" data-testid="price-calculator-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-10 pb-12 relative z-20">
          <PriceCalculatorHero />
        </div>
      </section>

      {/* === TRUST STRIP === */}
      <section className="bg-brand-dark text-white/70 border-t border-white/5" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-5 flex flex-wrap items-center justify-between gap-4 text-xs uppercase tracking-[0.18em]">
          <span className="flex items-center gap-2"><CheckCircle2 size={14} className="text-brand-red" /> CVR 46465717</span>
          <span className="flex items-center gap-2"><CheckCircle2 size={14} className="text-brand-red" /> Direkte A → B</span>
          <span className="flex items-center gap-2"><CheckCircle2 size={14} className="text-brand-red" /> Ingen omlastning</span>
          <span className="flex items-center gap-2"><CheckCircle2 size={14} className="text-brand-red" /> Sikker Stripe-betaling</span>
          <span className="flex items-center gap-2"><CheckCircle2 size={14} className="text-brand-red" /> Realtid sporing</span>
        </div>
      </section>

      {/* === HOW IT WORKS === */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-28" data-testid="how-it-works">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="kicker">Sådan virker det</div>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mt-4 text-brand-dark">
              Fra booking til levering<br/><span className="text-brand-red">direkte fra A til B</span>.
            </h2>
            <p className="text-brand-text2 mt-5 leading-relaxed max-w-md">
              Ingen omlastning, intet lager. Vores chauffør henter pakken hos dig og kører den direkte til modtageren — same-day, express eller aften. Booking tager under 2 minutter.
            </p>
            <Link to="/book" className="btn-primary mt-8" data-testid="how-cta">
              Start din booking <ArrowRight size={16} />
            </Link>
          </div>
          <div className="lg:col-span-7 space-y-4">
            {[
              { n: "01", i: Clock, t: "Vælg service & tidspunkt", d: "Same-Day, Express, Aftenlevering eller Erhvervskurér. Udfyld afhentnings- og leveringsadresse, kontaktoplysninger og pakke-info. Tager under 2 minutter." },
              { n: "02", i: CreditCard, t: "Betal sikkert online", d: "Fra 199 kr inkl. moms (inkl. 8 km). Hver ekstra km koster 6 kr (8 kr ved Express). Ekstra pakker: +50 kr. Stripe og MobilePay — bekræftelse + QR-kode sendes med det samme." },
              { n: "03", i: Truck, t: "Vi henter direkte hos dig", d: "Chaufføren møder op på den valgte afhentningsadresse. Du viser QR-koden på telefonen, chaufføren scanner — og pakken kører direkte videre til modtageren. Ingen mellemstop, intet lager." },
              { n: "04", i: Activity, t: "Følg leveringen live", d: "Realtid GPS-sporing fra afhentning til levering. Notifikation til både afsender og modtager når pakken er leveret." },
            ].map((s) => (
              <div key={s.n} className="bg-white border border-brand p-6 sm:p-7 flex gap-5 items-start group hover:border-brand-dark transition-colors" data-testid={`step-${s.n}`}>
                <div className="font-display font-bold text-3xl text-brand-red/30 group-hover:text-brand-red transition-colors min-w-[3.5rem]">{s.n}</div>
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <s.i size={18} className="text-brand-dark" strokeWidth={2.2} />
                    <h3 className="font-display font-semibold text-xl text-brand-dark">{s.t}</h3>
                  </div>
                  <p className="text-brand-text2 text-sm mt-2 leading-relaxed">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === WHY EMA / FEATURES === */}
      <section className="bg-brand-dark text-white py-20 lg:py-28" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <div className="kicker text-brand-red">Hvorfor EMA Kurer</div>
              <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mt-4">
                Professionel kurérservice<br/>bygget på direkte levering.
              </h2>
              <p className="text-white/70 mt-5 leading-relaxed">
                Ingen lager. Ingen terminal. Ingen omlastning. EMA Kurer er en moderne, fleksibel kurérvirksomhed der henter pakker direkte hos afsenderen og kører dem direkte til modtageren — same-day, express eller om aftenen.
              </p>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { i: Zap, t: "Direkte A → B", d: "Chaufføren kører pakken direkte fra afhentningsadressen til modtageren — ingen mellemstop, intet lager." },
                { i: Shield, t: "Verificeret & forsikret", d: "Dansk ApS, CVR 46465717. Fuldt sporbar arbejdsproces fra afhentning til levering." },
                { i: Activity, t: "Realtid sporing", d: "Live GPS-tidslinje med timestamps — fra QR-scan ved afhentning til underskrift ved levering." },
                { i: Lock, t: "Sikker betaling", d: "Stripe og MobilePay, GDPR-compliant, dansk persondatabeskyttelse." },
                { i: Headphones, t: "Direkte support", d: "Ingen call-centers. Skriv til kontakt@emakurer.dk — vi svarer." },
                { i: TrendingUp, t: "Last-mile platform", d: "QR-baseret afhentning, GPS-sporing, automatisk fakturering — alt i én smidig kurér-flow." },
              ].map((f) => (
                <div key={f.t} className="bg-white/[0.04] border border-white/10 p-6 hover:bg-white/[0.06] hover:border-white/20 transition-colors" data-testid={`feature-${f.t.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="w-10 h-10 bg-brand-red text-white flex items-center justify-center mb-4"><f.i size={18} strokeWidth={2} /></div>
                  <div className="font-display font-semibold text-lg">{f.t}</div>
                  <div className="text-white/60 text-sm mt-2 leading-relaxed">{f.d}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* === 4 SERVICE TYPES === */}
      <section className="bg-brand-bg2 border-y border-brand" data-testid="services-overview">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <div className="kicker">Vores kurérservices</div>
              <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mt-4 text-brand-dark">
                Fire måder at få<br/>din pakke <span className="text-brand-red">direkte</span> frem.
              </h2>
              <p className="text-brand-text2 mt-5 leading-relaxed max-w-xl">
                Vælg den kurér-type der passer din situation. Vi kører altid direkte fra afhentningsadressen til modtageren — uden mellemstop, lager eller terminal.
              </p>
            </div>
            <Link to="/services" data-testid="services-cta" className="btn-primary lg:col-span-5 lg:justify-self-end self-start lg:self-end">Se alle services <ArrowRight size={16} /></Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
            {[
              { i: Zap, k: "01", t: "Same-Day Kurér", d: "Bestil i dag, leveret samme dag. Direkte fra afhentningsadressen til modtageren.", price: "Fra 199 kr", color: "border-brand-dark" },
              { i: Clock, k: "02", t: "Express Kurér", d: "Levering inden 90 minutter. Til hasteopgaver hvor tid er kritisk.", price: "Fra 289 kr", color: "border-amber-500" },
              { i: Activity, k: "03", t: "Aftenlevering", d: "Afhentning og levering i tidsrummet 18:00–22:00. Når andre kurérer har lukket.", price: "Fra 249 kr", color: "border-indigo-700" },
              { i: TrendingUp, k: "04", t: "Erhvervskurér", d: "Faste kuréraftaler for virksomheder, webshops, butikker, klinikker og kontorer.", price: "Tilbud", color: "border-slate-700" },
            ].map((s) => (
              <div key={s.t} className={`bg-white border-l-2 ${s.color} border border-brand p-6 flex flex-col h-full hover:shadow-md transition-shadow`} data-testid={`service-card-${s.k}`}>
                <span className="text-brand-red font-display font-bold text-xs tracking-widest">{s.k}</span>
                <s.i size={22} className="text-brand-dark mt-4" />
                <div className="font-display font-semibold text-lg text-brand-dark mt-4">{s.t}</div>
                <div className="text-brand-text2 text-sm mt-2 leading-relaxed flex-1">{s.d}</div>
                <div className="mt-5 pt-4 border-t border-brand">
                  <div className="text-[10px] tracking-[0.18em] uppercase text-brand-text2">{s.price.includes("Tilbud") ? "Erhvervstilbud" : "Startpris"}</div>
                  <div className="font-display font-bold text-xl text-brand-dark mt-1">{s.price}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === BUSINESS CUSTOMERS === */}
      <section className="relative overflow-hidden bg-brand-dark text-white py-20 lg:py-28" data-testid="business-section">
        <div className="absolute inset-0 opacity-15">
          <img src={B2B_IMG} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="kicker text-brand-red">For erhverv · Faste kuréraftaler</div>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mt-4">
              Erhvervskurér<br/>på fast aftale.
            </h2>
            <p className="text-white/70 mt-5 leading-relaxed max-w-xl">
              Webshops, advokater, butikker, klinikker og restauranter på Sjælland bruger EMA Kurer som deres faste erhvervskurér. Direkte A→B-leveringer, faste afhentningstider og månedsfaktura — med konsolidering og rute-optimering når I har flere pakker på engang.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-8">
              {[
                "B2B-portal med Min Side",
                "CSV/Excel masseimport",
                "Labels 10×15 + A6 + QR",
                "Public tracking til modtager",
                "Pickup-manifests + PDF",
                "Failed-delivery + redelivery flow",
                "Månedligt faktura-grundlag",
                "Billy faktura-integration",
                "API + webhooks (HMAC-signed)",
                "Sandbox til IT-test",
              ].map((p) => (
                <div key={p} className="flex items-start gap-2 text-sm"><CheckCircle2 size={16} className="text-brand-red mt-0.5 flex-shrink-0" /> <span className="text-white/90">{p}</span></div>
              ))}
            </div>
            <div className="mt-9 flex flex-wrap gap-3">
              <Link to="/erhverv" data-testid="biz-cta-learn" className="btn-primary">Læs mere om EMA Erhverv <ArrowRight size={16} /></Link>
              <Link to="/erhverv/faa-tilbud" data-testid="biz-cta-quote" className="btn-ghost-light">Få tilbud</Link>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { tier: "Starter", subtitle: "Lav volumen", note: "Pay-as-you-go", highlight: false },
                { tier: "Growth", subtitle: "Voksende volumen", note: "Mest valgte", highlight: true },
                { tier: "Scale", subtitle: "Stor volumen + SLA", note: "Faste vinduer", highlight: false },
              ].map((p) => (
                <div key={p.tier} className={`${p.highlight ? "bg-brand-red text-white border-brand-red" : "bg-white/[0.04] border-white/10 text-white"} border p-6 relative`}>
                  {p.highlight && <div className="absolute -top-3 left-6 bg-white text-brand-red text-[10px] font-bold tracking-widest uppercase px-2 py-1">Anbefalet</div>}
                  <div className={`text-[10px] tracking-[0.22em] uppercase ${p.highlight ? "text-white/80" : "text-white/50"}`}>{p.note}</div>
                  <div className="font-display font-bold text-2xl mt-2">{p.tier}</div>
                  <div className={`text-sm mt-3 ${p.highlight ? "text-white/90" : "text-white/70"}`}>{p.subtitle}</div>
                  <div className={`text-[11px] mt-4 ${p.highlight ? "text-white/70" : "text-white/50"}`}>Volumenbaseret prisaftale</div>
                </div>
              ))}
            </div>
            <p className="text-white/50 text-xs mt-4 text-center">
              <Link to="/erhverv/faa-tilbud" className="underline hover:text-white">Kontakt os for en prisaftale baseret på jeres volumen</Link>
            </p>
          </div>
        </div>
      </section>

      {/* === COVERAGE === */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-28" data-testid="area-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <img src={STREET_IMG} alt="Sjælland" className="w-full aspect-[16/10] object-cover" />
          </div>
          <div className="lg:col-span-5 flex flex-col justify-center">
            <div className="kicker">Dækningsområde</div>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight leading-tight mt-4 text-brand-dark">
              Levering — kun på Sjælland.
            </h2>
            <p className="text-brand-text2 mt-5 leading-relaxed">
              Fra København til Helsingør, fra Roskilde til Næstved — vi kører hvor du har brug for os. Same-day levering kun inden for Sjællands region for at sikre fart og kvalitet.
            </p>
            <div className="grid grid-cols-2 gap-3 mt-7">
              {["København", "Frederiksberg", "Roskilde", "Helsingør", "Køge", "Næstved", "Hillerød", "Slagelse"].map((c) => (
                <div key={c} className="flex items-center gap-2 text-sm text-brand-dark"><MapPin size={14} className="text-brand-red" /> {c}</div>
              ))}
            </div>
            <Link to="/book" className="btn-secondary mt-8 self-start" data-testid="area-cta">Book til disse byer <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>

      {/* === REVIEWS / TRUSTPILOT === */}
      <ReviewsSection variant="home" />

      {/* === APP DOWNLOAD === */}
      <section className="bg-brand-bg2 border-y border-brand" data-testid="app-download-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="kicker">EMA Kurer App</div>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mt-4 text-brand-dark">
              Book, betal og spor direkte fra din lomme.
            </h2>
            <p className="text-brand-text2 mt-5 leading-relaxed max-w-xl">
              Hent vores app og få adgang til alt — book pakker, modtag besked når chaufføren er på vej, vis QR-koden direkte fra telefonen, og genbestil dine faste ruter med ét tap.
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-7 max-w-xl">
              {[
                "Live tracking med push-besked",
                "QR-kode altid ved hånden",
                "1-tap genbestilling",
                "Faktura-historik",
              ].map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-brand-dark">
                  <span className="w-1.5 h-1.5 bg-brand-red"></span> {f}
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-3 mt-8">
              <a href="#" className="inline-flex items-center gap-3 bg-brand-dark text-white px-5 py-3 hover:bg-brand-dark/90 transition" data-testid="app-cta-ios" aria-label="Hent på App Store">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
                <div className="leading-none text-left">
                  <div className="text-[10px] uppercase tracking-widest opacity-70">Hent på</div>
                  <div className="font-display font-semibold text-base mt-1">App Store</div>
                </div>
              </a>
              <a href="#" className="inline-flex items-center gap-3 bg-brand-dark text-white px-5 py-3 hover:bg-brand-dark/90 transition" data-testid="app-cta-android" aria-label="Hent på Google Play">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M3.61 1.81a1.5 1.5 0 0 0-.61 1.21v17.96a1.5 1.5 0 0 0 .61 1.21l9.66-10.19L3.61 1.81zm12.61 7.5L4.34 2.04l9.92 10.46 1.96-2.07zM4.34 21.96l11.88-7.27-1.96-2.07L4.34 21.96zm15.46-10.5l-2.43-1.4-2.21 2.33 2.21 2.33 2.43-1.4a1.5 1.5 0 0 0 0-1.86z"/></svg>
                <div className="leading-none text-left">
                  <div className="text-[10px] uppercase tracking-widest opacity-70">Hent på</div>
                  <div className="font-display font-semibold text-base mt-1">Google Play</div>
                </div>
              </a>
            </div>
            <div className="text-xs text-brand-text2 mt-4">Snart i App Store og Google Play · Få besked når den er klar: <a href="mailto:kontakt@emakurer.dk?subject=EMA Kurer-app" className="underline hover:text-brand-red">kontakt@emakurer.dk</a></div>
          </div>
          <div className="lg:col-span-5 relative">
            <div className="bg-brand-dark p-8 lg:p-10 aspect-[3/4] flex flex-col items-center justify-center text-white relative overflow-hidden">
              <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_top,#DC2626,transparent_60%)]"></div>
              <div className="relative">
                <div className="w-20 h-20 bg-brand-red flex items-center justify-center mb-6 mx-auto">
                  <Truck size={36} className="text-white" />
                </div>
                <div className="kicker text-brand-red text-center">EMA Kurer</div>
                <div className="font-display font-bold text-3xl text-center mt-2">Direkte A → B</div>
                <div className="text-white/60 text-sm text-center mt-3 max-w-[240px]">Same-day · Express · Aften · Erhverv</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* === FINAL CTA === */}
      <section className="bg-brand-dark text-white relative overflow-hidden" data-testid="bottom-cta">
        <div className="absolute inset-0 opacity-20">
          <img src={COURIER_IMG} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-24 lg:py-28 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <div className="kicker text-brand-red">Klar til kurérservice?</div>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mt-4">
              Book din kurér på under 2 minutter.
            </h2>
            <p className="text-white/70 mt-5 max-w-xl">
              Indtast afhentning, levering og pakke-info. Betal sikkert online. Du modtager bekræftelse med QR-kode — vis den til chaufføren ved afhentning.
            </p>
          </div>
          <div className="flex flex-wrap gap-4 lg:justify-end">
            <Link to="/book" data-testid="cta-book" className="btn-primary">Book kurér nu <ArrowRight size={18} /></Link>
            <Link to="/kontakt" data-testid="cta-contact" className="btn-ghost-light">Kontakt salg</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
